<template>
  <PageLayout>
    <template v-slot:header>
      <div class="d-flex m-3 mx-lg-0">
        <Breadcrumb :list="breadcrumbList" />
      </div>
      <MainNavbar />
    </template>
    <template v-slot:main>
      <b-card class="user-attribute-detail">
        <header slot="header">
          <b-row>
            <b-col md="12">
              {{ isEdit ? labels.edit : labels.create }}
            </b-col>
          </b-row>
        </header>
        <div v-if="loading" class="loading">
          <loading />
          <p>{{ labels.loading }}</p>
        </div>
        <div v-else>
          <b-row>
            <b-col
              v-for="(language, index) in languages"
              :key="`label_${index}`"
              class="pb-3"
              cols="12"
              xl="4"
            >
              <b-form-group>
                <b-form-input
                  class="user-attribute-form-input"
                  :value="userAttribute.label[language.code]"
                  @input="changeLanguageLabelProperty($event, language.code)"
                />
                <label class="custom-input-label">
                  <img
                    :src="`/icons/languages/${language.code}.svg`"
                    class="language-icon ml-2 mr-2"
                  />
                  {{ labels.label }}
                </label>
              </b-form-group>
            </b-col>
          </b-row>
          <div>
            <hr />
            <small>{{ labels.options }}</small>
            <div class="options-wrapper mt-2">
              <div class="no-content" v-if="userAttribute.values.length === 0">
                {{ labels.optionsNone }}
              </div>
              <div
                v-for="(value, index) in userAttribute.values"
                :key="`value_${index}`"
              >
                <b-row class="value-row">
                  <b-col cols="12" xl="1" class="move-value">
                    <small class="mr-2" v-if="value.id">
                      {{ value.id.substr(value.id.length - 4) }}
                    </small>
                    <small class="mr-2" v-else>
                      {{ `#${index + 1}` }}
                    </small>
                    <i
                      :title="labels.moveUp"
                      class="mdi mdi-chevron-up"
                      :class="{
                        'move-value-active': index > 0,
                        'text-gray07': index === 0,
                      }"
                      @click="moveUp(index)"
                    >
                    </i>
                    <i
                      :title="labels.moveDown"
                      class="mdi mdi-chevron-down mr-2"
                      :class="{
                        'move-value-active':
                          index + 1 < userAttribute.values.length,
                        'text-gray07':
                          index + 1 === userAttribute.values.length,
                      }"
                      @click="moveDown(index)"
                    >
                    </i>
                  </b-col>
                  <b-col
                    v-for="(language, idx) in languages"
                    :key="`value_${index}_label_${idx}`"
                    class="pb-3"
                    cols="12"
                    xl="3"
                  >
                    <b-form-group>
                      <b-form-input
                        class="user-attribute-form-input"
                        :value="value.label[language.code]"
                        @input="
                          changeLanguageValueLabelProperty(
                            index,
                            $event,
                            language.code
                          )
                        "
                      />
                      <label class="custom-input-label language-image">
                        <img
                          :src="`/icons/languages/${language.code}.svg`"
                          class="language-icon"
                        />
                      </label>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" xl="1" class="remove-value">
                    <i
                      v-if="userAttribute.values.length > 2"
                      :title="labels.optionsRemove"
                      @click="removeValue(index)"
                      class="material-icons clickable"
                    >
                      delete_forever
                    </i>
                  </b-col>
                </b-row>
                <hr class="d-xl-none" />
              </div>
            </div>
            <b-button
              variant="link"
              class="text-secondary mt-4"
              @click="addValue()"
            >
              <i class="material-icons">add</i>
              <small>
                {{ labels.optionsMore }}
              </small>
            </b-button>
          </div>
        </div>
        <b-list-group flush class="mt-4" v-if="!loading">
          <b-list-group-item>
            <b-row align-h="end">
              <b-button
                :title="labels.save"
                variant="primary"
                class="mt-3"
                :disabled="requestLoading"
                @click="saveUserAttribute"
              >
                {{ labels.save }}
              </b-button>
            </b-row>
          </b-list-group-item>
        </b-list-group>
      </b-card>
    </template>
  </PageLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import PageLayout from '@/components/Home/PageLayout.vue'
import Breadcrumb from '@/components/Shared/Breadcrumb.vue'
import { HIGH } from '@/helpers/messages-priority'
import Loading from '@/components/Shared/Loading.vue'
import MainNavbar from '@/components/Management/MainNavbar'

export default {
  components: {
    PageLayout,
    Breadcrumb,
    Loading,
    MainNavbar,
  },

  mounted() {
    this.$root.$emit('setPageTitle', { context: this, path: 'user-attributes' })
  },

  created() {
    this.$store.dispatch('userAttributes/clearUserAttribute')

    if (this.isEdit) {
      this.$store.dispatch(
        'userAttributes/loadUserAttribute',
        this.userAttributeId
      )
    }
  },

  data() {
    return {}
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
      languages: 'languages',
      loading: 'userAttributes/detailLoading',
      requestLoading: 'userAttributes/requestLoading',
      userAttribute: 'userAttributes/detailUserAttribute',
    }),

    breadcrumbList() {
      return [
        {
          label: this.resources.general.menu.settings,
        },
      ]
    },

    isEdit() {
      return this.userAttributeId !== 'new'
    },

    userAttributeId() {
      return this.$route.params.id
    },

    labels() {
      return this.resources.page.home.userAttributesDetail
    },
  },
  methods: {
    moveUp(index) {
      this.$store.dispatch('userAttributes/moveUpValue', { index })
    },
    moveDown(index) {
      this.$store.dispatch('userAttributes/moveDownValue', { index })
    },
    changeLanguageLabelProperty(value, language) {
      this.$store.dispatch('userAttributes/changeLanguageLabelProperty', {
        value,
        language,
      })
    },
    changeLanguageValueLabelProperty(index, value, language) {
      this.$store.dispatch('userAttributes/changeLanguageValueLabelProperty', {
        index,
        value,
        language,
      })
    },
    addValue() {
      this.$store.dispatch('userAttributes/addValue')
    },
    removeValue(index) {
      this.$store.dispatch('userAttributes/removeValue', index)
    },
    saveUserAttribute() {
      const { label, values } = this.userAttribute

      const labelsLanguages = Object.keys(label)
      const valuesLanguages = Object.keys(
        values.reduce((map, v) => {
          const languages = Object.keys(v.label)
          languages.forEach(l => (map[l] = true))
          return map
        }, {})
      )

      // validate label
      if (
        labelsLanguages.length === 0 ||
        !valuesLanguages.every(l => labelsLanguages.includes(l))
      ) {
        return this.$root.$emit('app:message:show', {
          priority: HIGH,
          text: this.labels.validationLabel,
          status: 'danger',
        })
      }

      // validate values
      const validationErrors = []
      labelsLanguages.forEach(language => {
        const isValid = values.every(v => v.label[language])
        if (!isValid) {
          validationErrors.push(language)
        }
      })

      if (validationErrors.length > 0) {
        return this.$root.$emit('app:message:show', {
          priority: HIGH,
          text: this.labels.validationValues,
          status: 'danger',
        })
      }

      // start save update
      this.$store.dispatch('userAttributes/setRequestLoading', true)

      const response = this.isEdit
        ? this.$store.dispatch('userAttributes/update', this.userAttribute)
        : this.$store.dispatch('userAttributes/save', this.userAttribute)

      response
        .then(() => {
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.isEdit
              ? this.labels.saveEditSuccessMessage
              : this.labels.saveCreateSuccessMessage,
            status: 'success',
          })
          this.$store.dispatch('userAttributes/setRequestLoading', false)
          this.$router.push({ name: 'user-attributes' })
        })
        .catch(error => {
          const { response: { data = {} } = {} } = error
          this.$root.$emit('app:message:show', {
            priority: HIGH,
            text: this.isEdit
              ? `${this.labels.saveEditErrorMessage} ${data.message}`
              : `${this.labels.saveCreateErrorMessage} ${data.message}`,
            status: 'danger',
          })
          this.$store.dispatch('userAttributes/setRequestLoading', false)
        })
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.user-attribute-detail {
  .btn {
    border-radius: 10px !important;
    min-width: 160px;
  }

  .options-wrapper {
    .value-row {
      .move-value {
        display: flex;
        align-items: center;
        font-size: 1.2em;

        .move-value-active {
          cursor: pointer;
        }
      }

      .language-image {
        top: -10px !important;
      }

      .remove-value {
        display: flex;
        align-items: center;
        font-size: 1.5em;
      }
    }
  }

  .language-icon {
    width: 12px;
    height: 12px;
  }

  .loading {
    text-align: center;
    width: 100%;
  }

  .user-attribute-form-input {
    border-radius: 10px !important;
    background-color: map-get($theme-colors, 'white') !important;
    border-color: var(--gray07) !important;
  }

  .no-content {
    text-align: center;
    padding-top: 50px;
    padding-bottom: 40px;
  }
}
</style>
